import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = loading, null = not logged, object = logged
    const [error, setError] = useState(null);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        // Only hit /me if we have a token (avoids noisy 401 for anonymous visitors)
        const hasToken = typeof window !== "undefined" && localStorage.getItem("bc_access_token");
        if (hasToken) {
            refreshMe();
        } else {
            setUser(null);
        }
    }, [refreshMe]);

    const login = async (email, password) => {
        setError(null);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.access_token) {
                localStorage.setItem("bc_access_token", data.access_token);
            }
            setUser(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            const msg = formatApiError(e);
            setError(msg);
            return { ok: false, error: msg };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* ignore */
        }
        localStorage.removeItem("bc_access_token");
        setUser(null);
    };

    const register = async (payload) => {
        setError(null);
        try {
            const { data } = await api.post("/auth/register", payload);
            return { ok: true, data };
        } catch (e) {
            const msg = formatApiError(e);
            setError(msg);
            return { ok: false, error: msg };
        }
    };

    return (
        <AuthContext.Provider value={{ user, error, login, logout, register, refreshMe, setError }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
